import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Logo, Check, Sparkles } from '../components/Icons.jsx';
import { Intro, Done, mainStyle, formatTime } from './VennArchitect.jsx';
import { TutorNudge } from '../components/TutorCard.jsx';
import { tutorFor } from '../lib/tutors.js';
import DrillResultBar from '../components/DrillResultBar.jsx';
import StuckPopIn from '../components/StuckPopIn.jsx';

const easyQs = [
  { q: 'A bag has 5 red and 3 blue marbles. P(red)?', options: ['5/8', '3/8', '5/3', '1/2'], answer: 0, working: '5 reds out of 8 total.' },
  { q: 'Single die roll. P(even number)?', options: ['1/6', '1/3', '1/2', '2/3'], answer: 2, working: 'Evens are 2, 4, 6 → 3/6 = 1/2.' },
  { q: 'A coin is flipped twice. P(two heads)?', options: ['1/4', '1/2', '1/3', '3/4'], answer: 0, working: '1/2 × 1/2 = 1/4. Independent events.' },
  { q: 'A clinic has 100 patients: 40 male, 60 female. P(male picked at random)?', options: ['40%', '60%', '50%', '25%'], answer: 0, working: '40/100 = 40%.' },
  { q: 'Card from a standard deck. P(face card — J, Q, K)?', options: ['1/13', '3/13', '1/4', '12/52'], answer: 1, working: '12 face cards / 52 = 3/13.' },
  { q: 'P(rolling a 6 on a die OR flipping heads on a coin)?', options: ['1/6', '1/12', '1/2', '7/12'], answer: 3, working: 'P(A) + P(B) − P(A∩B) = 1/6 + 1/2 − 1/12 = 7/12.' }
];

const hardQs = [
  { q: 'P(at least one head in 3 coin flips)?', options: ['1/8', '3/8', '7/8', '1/2'], answer: 2, working: '1 − P(all tails) = 1 − (1/2)^3 = 1 − 1/8 = 7/8. Complement trick.' },
  { q: 'Two dice rolled. P(sum = 7)?', options: ['1/6', '1/12', '6/36', '7/36'], answer: 0, working: '6 combinations (1+6, 2+5, ...) of 36 = 1/6.' },
  { q: 'A class is 60% female. Of females, 30% wear glasses. P(female AND wears glasses)?', options: ['18%', '30%', '60%', '90%'], answer: 0, working: '0.6 × 0.3 = 0.18 = 18%. Multiply conditionals.' },
  { q: 'A medical test has 95% sensitivity and 90% specificity. Disease prevalence 5%. P(positive test | disease)?', options: ['5%', '50%', '90%', '95%'], answer: 3, working: 'Sensitivity = P(test+ | disease) = 95%. The other numbers are distractors.' },
  { q: 'Roll two dice. P(at least one 6)?', options: ['1/3', '11/36', '2/12', '1/6'], answer: 1, working: '1 − P(no 6 on either) = 1 − (5/6)^2 = 1 − 25/36 = 11/36.' },
  { q: 'A drug trial: 200 patients. 120 took drug; 70 of those improved. 80 took placebo; 30 improved. P(improvement given drug)?', options: ['35%', '37.5%', '58.3%', '50%'], answer: 2, working: '70/120 = 58.3%. Conditional probability.' },
  { q: 'You buy a £2 ticket. 1/100 chance of winning £150. Expected value of holding the ticket?', options: ['£1.50', '−£0.50', '£0.50', '£148'], answer: 0, working: '£150 × 0.01 = £1.50 expected value (before subtracting cost).' },
  { q: 'Bookies offer odds of 4-to-1 against an event. Implied probability?', options: ['25%', '20%', '40%', '50%'], answer: 1, working: '4-to-1 against = 1 / (4+1) = 1/5 = 20% implied probability.' }
];

export default function ProbabilityReasoning() {
  const [stage, setStage] = useState('intro');
  const [difficulty, setDifficulty] = useState('easy');
  const [length, setLength] = useState(8);
  const [ucatsen, setUcatsen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [picked, setPicked] = useState(null);

  const pool = difficulty === 'easy' ? easyQs : hardQs;
  const questions = useMemo(() => {
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(length, pool.length));
  }, [pool, length, stage]);
  const q = questions[idx];

  const start = () => { setIdx(0); setAnswers([]); setPicked(null); setStage('drill'); };
  const choose = (i) => setPicked(i);
  const next = () => {
    const correct = picked === q.answer;
    const nextAnswers = [...answers, { question: q.q, chosen: picked, correct, working: q.working, options: q.options, answer: q.answer }];
    setAnswers(nextAnswers);
    setPicked(null);
    if (idx < questions.length - 1) setIdx(idx + 1);
    else setStage('done');
  };

  const correctCount = answers.filter((a) => a.correct).length;
  let wrongStreak = 0;
  for (let i = answers.length - 1; i >= 0; i--) {
    if (answers[i].correct) break;
    wrongStreak++;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)' }}>
        <Link to="/practice" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
          <ArrowLeft width="16" height="16" />Exit
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight />
          <strong style={{ fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' }}>Probability reasoning</strong>
        </div>
        <div style={{ width: 60 }} />
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="DM" title="Probability reasoning"
          body="UCAT-style probability: single events, AND/OR, complement, conditional, expected value and odds."
          how={['Pick difficulty + number of questions', 'Tap your answer and Next to move on', 'Per-question rationale shown at the end']}
          difficulty={difficulty} setDifficulty={setDifficulty}
          difficultyOptions={[
            { v: 'easy', label: 'Foundation', sub: 'Single events, basic AND/OR' },
            { v: 'hard', label: 'UCAT-style', sub: 'Conditional, complement, EV, odds' }
          ]}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
          extra={
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Length</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[5, 8, 10].map((n) => (
                  <button key={n} onClick={() => setLength(n)} style={{
                    padding: '8px 16px', borderRadius: 99, cursor: 'pointer', fontSize: 14, fontWeight: 600,
                    border: `1px solid ${length === n ? 'var(--brand-purple)' : 'var(--border)'}`,
                    background: length === n ? 'var(--accent-soft)' : '#fff',
                    color: length === n ? 'var(--brand-deep)' : 'var(--text-2)'
                  }}>{n} questions</button>
                ))}
              </div>
            </div>
          }
        />
      )}

      {stage === 'drill' && <StuckPopIn wrongStreak={wrongStreak} slug="probability-reasoning" sectionLabel="probability reasoning" />}
      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>Question {idx + 1} of {questions.length}</span>
            <span>{difficulty === 'easy' ? 'Foundation' : 'UCAT-style'}</span>
          </div>
          <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, marginBottom: 22 }}>
            <div style={{ width: `${((idx + 1) / questions.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', borderRadius: 99 }} />
          </div>

          <h2 style={{ margin: '0 0 22px', fontSize: 'clamp(18px, 2.6vw, 22px)', lineHeight: 1.45 }}>{q.q}</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
            {q.options.map((opt, i) => (
              <button key={i} onClick={() => choose(i)} style={{
                padding: '14px 18px', textAlign: 'left', fontSize: 15.5, fontWeight: 600, cursor: 'pointer',
                border: `2px solid ${picked === i ? 'var(--brand-purple)' : 'var(--border)'}`,
                background: picked === i ? 'var(--accent-soft)' : '#fff',
                color: 'var(--text)', borderRadius: 12,
                display: 'flex', alignItems: 'center', gap: 12
              }}>
                <span style={{ width: 26, height: 26, borderRadius: '50%', background: picked === i ? 'var(--brand-purple)' : 'var(--card-soft)', color: picked === i ? '#fff' : 'var(--text-2)', display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  {String.fromCharCode(65 + i)}
                </span>
                {opt}
              </button>
            ))}
          </div>

          <button onClick={next} disabled={picked === null} className="btn btn-primary" style={{ opacity: picked === null ? 0.5 : 1 }}>
            {idx === questions.length - 1 ? 'Finish' : 'Next'} <ArrowRight width="14" height="14" />
          </button>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="DM" title="Probability reasoning" stats={[
          { label: 'Correct', value: `${correctCount} / ${questions.length}` },
          { label: 'Accuracy', value: `${Math.round((correctCount / questions.length) * 100)}%` },
          { label: 'Mode', value: difficulty === 'easy' ? 'Foundation' : 'UCAT-style' }
        ]} onRestart={start}
          extra={<>
            <DrillResultBar slug="probability-reasoning" score={correctCount} maxScore={questions.length} accuracyPct={Math.round((correctCount / questions.length) * 100)} metadata={{ difficulty, length, ucatsen }} />
            {correctCount < questions.length * 0.65 && (
              <TutorNudge
                tutor={tutorFor('probability')}
                reason="DM probability questions are pattern-heavy. A DM specialist can map the 5 most common patterns in 90 mins."
                kicker="Probability tripping you up?"
              />
            )}
            <div style={{ marginTop: 18, marginBottom: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {answers.map((a, i) => (
                <div key={i} className="card" style={{
                  borderLeft: `4px solid ${a.correct ? 'var(--live-green)' : '#b91c1c'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                    <strong style={{ fontSize: 14 }}>Q{i + 1}. {a.question}</strong>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 700, flexShrink: 0,
                      background: a.correct ? '#e7f7ef' : '#fee2e2',
                      color: a.correct ? 'var(--live-green)' : '#b91c1c'
                    }}>{a.correct ? 'Correct' : 'Wrong'}</span>
                  </div>
                  <div style={{ fontSize: 13.5, color: 'var(--text-2)' }}>
                    Your answer: <strong>{a.options[a.chosen]}</strong> · Correct: <strong>{a.options[a.answer]}</strong>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, color: 'var(--text-2)', padding: '8px 12px', background: 'var(--card-soft)', borderRadius: 8 }}>
                    {a.working}
                  </div>
                </div>
              ))}
            </div>
          </>}
        />
      )}
    </div>
  );
}
